import createDebug from 'debug';
import {
	AlarmClock,
	Archive,
	Calendar,
	Check,
	ChevronDown,
	Copy,
	ChevronUp,
	Database,
	Download,
	ExternalLink,
	Inbox,
	Layers,
	Loader2,
	Pencil,
	RefreshCw,
	RotateCcw,
	Share2,
	Trash2,
	X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { LinkResponse, QueueResponse } from '@/api/client';
import { MarkdownContent } from '@/components/MarkdownContent';
import { type TagData, TagList } from '@/components/TagDisplay';
import { useAuth } from '@/contexts/auth';

const debug = createDebug('app:detail-panel');

import { DownloadSection, type DownloadSectionHandle } from '@/components/DownloadSection';
import { EnrichOptionsModal } from '@/components/EnrichOptionsModal';
import { ErrorDialog } from '@/components/ErrorDialog';
import { LinkPublicAccessPanel } from '@/components/LinkPublicAccessPanel';
import { ShareLinkDialog } from '@/components/ShareLinkDialog';
import { SnoozeDurationPicker } from '@/components/SnoozeDurationPicker';
import { TagEditor } from '@/components/TagEditor';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ActionTooltip } from '@/components/ui/tooltip';
import { useKeyboardShortcuts } from '@/hooks/useKeyboardShortcuts';
import { useArchiveLink, useEnrichLink, useSnoozeLink, useUnarchiveLink, useUpdateLink } from '@/hooks/useLinks';
import { cn } from '@/lib/utils';

// YAML-style object viewer with syntax highlighting (high contrast for debugging)
function YamlValue({ value }: { value: unknown }) {
	if (value === null) return <span className="text-zinc-400">null</span>;
	if (value === undefined) return <span className="text-zinc-400">~</span>;
	if (typeof value === 'boolean')
		return <span className="font-bold text-cyan-300">{value.toString()}</span>;
	if (typeof value === 'number')
		return <span className="text-emerald-400">{value}</span>;
	if (typeof value === 'string') {
		if (value.startsWith('http://') || value.startsWith('https://')) {
			return (
				<a
					href={value}
					target="_blank"
					rel="noopener noreferrer"
					className="text-pink-400 hover:text-pink-300 hover:underline break-all"
				>
					{value}
				</a>
			);
		}
		return <span className="text-gray-200 break-all">{value}</span>;
	}
	return <span className="text-gray-200">{String(value)}</span>;
}

function YamlNode({
	name,
	value,
	isArrayItem = false,
}: {
	name?: string;
	value: unknown;
	isArrayItem?: boolean;
}) {
	const isObject = value !== null && typeof value === 'object' && !Array.isArray(value);
	const isArray = Array.isArray(value);
	const isComplex = isObject || isArray;

	// Array item prefix
	const prefix = isArrayItem ? (
		<span className="text-cyan-400 mr-1">-</span>
	) : null;

	// Key (if named)
	const keyEl = name ? (
		<span className="font-bold text-amber-400 mr-1">{name}:</span>
	) : null;

	// Simple value on same line
	if (!isComplex) {
		return (
			<div className="flex">
				{prefix}
				{keyEl}
				<YamlValue value={value} />
			</div>
		);
	}

	// Empty array/object
	if (isArray && (value as unknown[]).length === 0) {
		return (
			<div>
				{prefix}
				{keyEl}
				<span className="text-zinc-400">[]</span>
			</div>
		);
	}
	if (isObject && Object.keys(value as object).length === 0) {
		return (
			<div>
				{prefix}
				{keyEl}
				<span className="text-zinc-400">{'{}'}</span>
			</div>
		);
	}

	// Complex value: key on own line, children indented
	return (
		<div>
			<div>
				{prefix}
				{keyEl}
			</div>
			<div className="pl-4">
				{isArray
					? (value as unknown[]).map((item, i) => <YamlNode key={i} value={item} isArrayItem />)
					: Object.entries(value as object).map(([k, v]) => (
							<YamlNode key={k} name={k} value={v} />
						))}
			</div>
		</div>
	);
}

function ObjectViewer({ data }: { data: Record<string, unknown> }) {
	return (
		<div className="mt-2 overflow-auto border border-border bg-background p-3 text-xs font-mono leading-relaxed">
			{Object.entries(data).map(([k, v]) => (
				<YamlNode key={k} name={k} value={v} />
			))}
		</div>
	);
}

type LinkDetailPanelProps = {
	link: LinkResponse | null;
	queues: QueueResponse[];
	onLinkUpdated?: (link: LinkResponse) => void;
	onLinkArchived?: (linkId: number) => void;
	onLinkUnarchived?: (linkId: number) => void;
	onTagClick?: (tag: TagData) => void;
	onNext?: () => void;
	onPrevious?: () => void;
	onShowShortcuts?: () => void;
	onQueueChange?: () => void;
	hasNext?: boolean;
	hasPrevious?: boolean;
	className?: string;
};

function CopyUrlButton({ url }: { url: string }) {
	const [copied, setCopied] = useState(false);

	async function handleCopy() {
		try {
			await navigator.clipboard.writeText(url);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		} catch {
			// Fallback for older browsers
			const textarea = document.createElement('textarea');
			textarea.value = url;
			textarea.style.position = 'fixed';
			textarea.style.opacity = '0';
			document.body.appendChild(textarea);
			textarea.select();
			document.execCommand('copy');
			document.body.removeChild(textarea);
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		}
	}

	return (
		<button
			onClick={handleCopy}
			className="opacity-0 group-hover/url:opacity-50 hover:!opacity-100 transition-opacity ml-1"
			title={copied ? 'Copied!' : 'Copy URL'}
		>
			{copied ? <Check className="size-3 text-green-400" /> : <Copy className="size-3" />}
		</button>
	);
}

export function LinkDetailPanel({
	link,
	queues,
	onLinkUpdated,
	onLinkArchived,
	onLinkUnarchived,
	onTagClick,
	onNext,
	onPrevious,
	onShowShortcuts,
	onQueueChange,
	hasNext = false,
	hasPrevious = false,
	className,
}: LinkDetailPanelProps) {
	const { user } = useAuth();
	const updateLink = useUpdateLink();
	const archiveLink = useArchiveLink();
	const unarchiveLinkMutation = useUnarchiveLink();
	const enrichLinkMutation = useEnrichLink();
	const snoozeLinkMutation = useSnoozeLink();
	const [isArchiving, setIsArchiving] = useState(false);
	const [isUnarchiving, setIsUnarchiving] = useState(false);
	const [isEnriching, setIsEnriching] = useState(false);
	const [isSnoozing, setIsSnoozing] = useState(false);
	const [showTagEditor, setShowTagEditor] = useState(false);
	const [showEnrichOptions, setShowEnrichOptions] = useState(false);
	const [showShareDialog, setShowShareDialog] = useState(false);
	const [showSnoozePicker, setShowSnoozePicker] = useState(false);
	const downloadSectionRef = useRef<DownloadSectionHandle>(null);
	const [enrichError, setEnrichError] = useState<{
		error: Error | string;
		details?: string;
	} | null>(null);

	// Edit states
	const [isEditingTitle, setIsEditingTitle] = useState(false);
	const [editTitle, setEditTitle] = useState('');
	const [isEditingUrl, setIsEditingUrl] = useState(false);
	const [editUrl, setEditUrl] = useState('');
	const [isEditingDescription, setIsEditingDescription] = useState(false);
	const [editDescription, setEditDescription] = useState('');

	const titleInputRef = useRef<HTMLInputElement>(null);
	const urlInputRef = useRef<HTMLInputElement>(null);
	const descriptionRef = useRef<HTMLTextAreaElement>(null);

	// Reset edit state when link changes
	useEffect(() => {
		if (link) {
			setEditTitle(link.title);
			setEditUrl(link.url);
			setEditDescription(link.description || '');
			setIsEditingTitle(false);
			setIsEditingUrl(false);
			setIsEditingDescription(false);
		}
	}, [link?.id, link?.description, link]);

	// Focus inputs when entering edit mode
	useEffect(() => {
		if (isEditingTitle && titleInputRef.current) {
			titleInputRef.current.focus();
			titleInputRef.current.select();
		}
	}, [isEditingTitle]);

	useEffect(() => {
		if (isEditingUrl && urlInputRef.current) {
			urlInputRef.current.focus();
			urlInputRef.current.select();
		}
	}, [isEditingUrl]);

	useEffect(() => {
		if (isEditingDescription && descriptionRef.current) {
			descriptionRef.current.focus();
		}
	}, [isEditingDescription]);

	// Keyboard actions
	const openLink = useCallback(() => {
		if (link) window.open(link.url, '_blank');
	}, [link]);

	const archiveAndNext = useCallback(async () => {
		if (!link) return;
		await archiveLink.mutateAsync(link.id);
		onLinkArchived?.(link.id);
		onNext?.();
	}, [link, archiveLink, onNext, onLinkArchived]);

	const archiveAndPrevious = useCallback(async () => {
		if (!link) return;
		await archiveLink.mutateAsync(link.id);
		onLinkArchived?.(link.id);
		onPrevious?.();
	}, [link, archiveLink, onPrevious, onLinkArchived]);

	const openTagEditor = useCallback(() => {
		setShowTagEditor(true);
	}, []);

	const openQueueSelector = useCallback(() => {
		onQueueChange?.();
	}, [onQueueChange]);

	// Define keyboard shortcuts
	const shortcuts = useMemo(
		() => [
			{ key: 'j', description: 'Next link', action: () => onNext?.() },
			{ key: 'ArrowDown', description: 'Next link', action: () => onNext?.() },
			{ key: 'k', description: 'Previous link', action: () => onPrevious?.() },
			{ key: 'ArrowUp', description: 'Previous link', action: () => onPrevious?.() },
			{ key: 'o', description: 'Open link', action: openLink },
			{ key: 'Enter', description: 'Open link', action: openLink },
			{
				key: 'e',
				description: 'Archive',
				action: () => {
					if (link) {
						archiveLink.mutateAsync(link.id).then(() => onLinkArchived?.(link.id));
					}
				},
			},
			{ key: 'l', description: 'Edit tags', action: openTagEditor },
			{ key: 'q', description: 'Change queue', action: openQueueSelector },
			{ key: ']', description: 'Archive + next', action: archiveAndNext },
			{ key: '[', description: 'Archive + previous', action: archiveAndPrevious },
			{ key: 'd', description: 'Download', action: () => downloadSectionRef.current?.trigger() },
			{ key: '?', description: 'Show shortcuts', action: () => onShowShortcuts?.() },
		],
		[
			link,
			onNext,
			onPrevious,
			openLink,
			archiveLink,
			openTagEditor,
			openQueueSelector,
			archiveAndNext,
			archiveAndPrevious,
			onShowShortcuts,
			onLinkArchived,
		]
	);

	// Register keyboard shortcuts - only when panel has a link and not editing
	useKeyboardShortcuts({
		enabled: !!link && !isEditingTitle && !isEditingUrl && !isEditingDescription && !showTagEditor,
		shortcuts,
	});

	// Get current queue info
	const currentQueue = useMemo(() => {
		if (!link) return null;
		if (link.queueId === 0) {
			return { id: 0, slug: 'incoming', name: 'Incoming', color: '#6366f1', icon: null };
		}
		return queues.find((q) => q.id === link.queueId) ?? null;
	}, [link, queues]);

	// Empty state
	if (!link) {
		return (
			<div
				className={cn('flex flex-col items-center justify-center text-muted-foreground', className)}
			>
				<Inbox className="size-12 mb-4 opacity-50" />
				<p className="text-lg font-medium">No link selected</p>
				<p className="text-sm">Click a link to view details</p>
			</div>
		);
	}

	function handleTitleSave() {
		const trimmedTitle = editTitle.trim();
		if (trimmedTitle && trimmedTitle !== link!.title) {
			updateLink.mutate(
				{ id: link!.id, data: { title: trimmedTitle } },
				{ onSuccess: () => onLinkUpdated?.({ ...link!, title: trimmedTitle }) }
			);
		}
		setIsEditingTitle(false);
	}

	function handleTitleCancel() {
		setEditTitle(link!.title);
		setIsEditingTitle(false);
	}

	function handleTitleKeyDown(e: React.KeyboardEvent) {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleTitleSave();
		} else if (e.key === 'Escape') {
			handleTitleCancel();
		}
	}

	function handleUrlSave() {
		const trimmed = editUrl.trim();
		if (trimmed && trimmed !== link!.url) {
			try {
				new URL(trimmed);
			} catch {
				setEditUrl(link!.url);
				setIsEditingUrl(false);
				return;
			}
			updateLink.mutate(
				{ id: link!.id, data: { url: trimmed } },
				{ onSuccess: () => onLinkUpdated?.({ ...link!, url: trimmed }) }
			);
		}
		setIsEditingUrl(false);
	}

	function handleUrlCancel() {
		setEditUrl(link!.url);
		setIsEditingUrl(false);
	}

	function handleUrlKeyDown(e: React.KeyboardEvent) {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleUrlSave();
		} else if (e.key === 'Escape') {
			handleUrlCancel();
		}
	}

	function handleDescriptionSave() {
		const newDescription = editDescription.trim() || null;
		if (newDescription !== (link!.description || null)) {
			const desc = newDescription || '';
			updateLink.mutate(
				{ id: link!.id, data: { description: desc } },
				{
					onSuccess: () => {
						onLinkUpdated?.({ ...link!, description: desc });
						setEditDescription(desc);
						setIsEditingDescription(false);
					},
				}
			);
		} else {
			setIsEditingDescription(false);
		}
	}

	function handleDescriptionCancel() {
		setEditDescription(link!.description || '');
		setIsEditingDescription(false);
	}

	function handleDescriptionKeyDown(e: React.KeyboardEvent) {
		if (e.key === 'Escape') {
			handleDescriptionCancel();
		}
		if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
			e.preventDefault();
			handleDescriptionSave();
		}
	}

	async function handleArchive() {
		setIsArchiving(true);
		try {
			await archiveLink.mutateAsync(link!.id);
			onLinkArchived?.(link!.id);
		} finally {
			setIsArchiving(false);
		}
	}

	async function handleUnarchive() {
		setIsUnarchiving(true);
		try {
			await unarchiveLinkMutation.mutateAsync(link!.id);
			onLinkUnarchived?.(link!.id);
		} finally {
			setIsUnarchiving(false);
		}
	}

	async function handleEnrich(flags?: number) {
		setIsEnriching(true);
		setEnrichError(null);
		try {
			debug('enrich: starting id=%d url=%s flags=%s', link!.id, link!.url, flags);
			const options = flags !== undefined ? { flags } : undefined;
			const result = await enrichLinkMutation.mutateAsync({ id: link!.id, options });
			debug('enrich: queued=%s', result?.queued);
			// Enrich only queues the work - update isEnriching flag on existing link
			// Don't replace the link with the sparse mutation response (it only has id + isEnriching)
			onLinkUpdated?.({ ...link!, isEnriching: true });
		} catch (error) {
			debug('enrich: failed %o', error);
			setEnrichError({
				error: error instanceof Error ? error : String(error),
				details: `Link ID: ${link!.id}\nURL: ${link!.url}`,
			});
		} finally {
			setIsEnriching(false);
		}
	}

	function handleEnrichClick(e: React.MouseEvent) {
		if (e.shiftKey) {
			// SHIFT+click: open options modal
			e.preventDefault();
			setShowEnrichOptions(true);
		} else {
			// Normal click: immediate full enrichment
			handleEnrich();
		}
	}

	async function handleSnooze(durationSeconds: number) {
		setIsSnoozing(true);
		try {
			const snoozedLink = await snoozeLinkMutation.mutateAsync(link!.id, durationSeconds);
			if (snoozedLink) {
				onLinkUpdated?.({ ...link!, snoozedUntil: snoozedLink.snoozedUntil });
			}
		} catch (error) {
			debug('snooze: failed %o', error);
		} finally {
			setIsSnoozing(false);
		}
	}

	const createdDate = new Date(link.createdAt).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});

	return (
		<div className={cn('flex flex-col h-full border-l border-border bg-background', className)}>
			{/* Header with navigation */}
			<div className="flex items-center justify-between border-b border-border px-4 py-2">
				<div className="flex items-center gap-1">
					<Button
						variant="ghost"
						size="icon"
						className="size-8"
						onClick={onPrevious}
						disabled={!hasPrevious}
						title="Previous (k)"
					>
						<ChevronUp className="size-4" />
					</Button>
					<Button
						variant="ghost"
						size="icon"
						className="size-8"
						onClick={onNext}
						disabled={!hasNext}
						title="Next (j)"
					>
						<ChevronDown className="size-4" />
					</Button>
				</div>
				<div className="flex items-center gap-2">
					<button
						type="button"
						className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors"
						onClick={() => navigator.clipboard.writeText(String(link.id))}
						title="Click to copy Link ID"
					>
						[{link.id}]
					</button>
					<ActionTooltip
						description="Re-enrich this link. Hold SHIFT for options."
						shortcut="R"
					>
						<Button
							variant="outline"
							size="sm"
							className="text-xs font-mono"
							onClick={handleEnrichClick}
							disabled={isEnriching}
						>
							{isEnriching ? (
								<Loader2 className="size-3 mr-1 animate-spin" />
							) : (
								<RefreshCw className="size-3 mr-1" />
							)}
							{isEnriching ? 'ENRICHING...' : '[ENRICH]'}
						</Button>
					</ActionTooltip>
					{user && (
						<ActionTooltip description="Share this link with other users">
							<Button
								variant="outline"
								size="sm"
								className="text-xs font-mono"
								onClick={() => setShowShareDialog(true)}
							>
								<Share2 className="size-3 mr-1" />
								[SHARE]
							</Button>
						</ActionTooltip>
					)}
					<ActionTooltip description="Download media (SHIFT for tool picker)" shortcut="D">
						<Button
							variant="outline"
							size="sm"
							className="text-xs font-mono"
							onClick={() => downloadSectionRef.current?.trigger()}
						>
							<Download className="size-3 mr-1" />
							[DL]
						</Button>
					</ActionTooltip>
					{!link.archivedAt && (
						<ActionTooltip description="Snooze this link to hide it temporarily" shortcut="Z">
							<Button
								variant="outline"
								size="sm"
								className="text-xs font-mono"
								onClick={() => setShowSnoozePicker(true)}
								disabled={isSnoozing}
							>
								{isSnoozing ? (
									<Loader2 className="size-3 mr-1 animate-spin" />
								) : (
									<AlarmClock className="size-3 mr-1" />
								)}
								{isSnoozing ? 'SNOOZING...' : '[SNOOZE]'}
							</Button>
						</ActionTooltip>
					)}
					<ActionTooltip description="Open original URL in new tab" shortcut="O">
						<Button
							variant="outline"
							size="sm"
							className="text-xs font-mono"
							onClick={() => window.open(link.url, '_blank')}
						>
							<ExternalLink className="size-3 mr-1" />
							[OPEN]
						</Button>
					</ActionTooltip>
					{link.archivedAt ? (
						<ActionTooltip description="Restore this link from archive">
							<Button
								variant="outline"
								size="sm"
								className="text-xs font-mono"
								onClick={handleUnarchive}
								disabled={isUnarchiving}
							>
								<RotateCcw className="size-3 mr-1" />
								{isUnarchiving ? 'RESTORING...' : '[RESTORE]'}
							</Button>
						</ActionTooltip>
					) : (
						<ActionTooltip description="Archive this link" shortcut="E">
							<Button
								variant="destructive"
								size="sm"
								className="text-xs font-mono"
								onClick={handleArchive}
								disabled={isArchiving}
							>
								<Trash2 className="size-3 mr-1" />
								{isArchiving ? 'ARCHIVING...' : '[ARCHIVE]'}
							</Button>
						</ActionTooltip>
					)}
				</div>
			</div>

			{/* Content */}
			<ScrollArea className="flex-1">
				<div className="p-4 space-y-4">
					{/* Header with optional floating thumbnail */}
					<div className="relative">
						{/* Thumbnail - floating right */}
						{link.thumbnailUrl && (
							<a
								href={link.thumbnailUrl}
								target="_blank"
								rel="noopener noreferrer"
								className="float-right ml-4 mb-2"
							>
								<img
									src={`/api/thumbnail/${link.id}`}
									alt=""
									className="max-w-[200px] max-h-[200px] rounded-lg object-contain bg-muted hover:opacity-90 transition-opacity cursor-pointer"
									loading="lazy"
									onError={(e) => {
										(e.target as HTMLImageElement).style.display = 'none';
									}}
								/>
							</a>
						)}

						{/* Title */}
						{isEditingTitle ? (
							<div className="flex items-center gap-2">
								<input
									ref={titleInputRef}
									type="text"
									value={editTitle}
									onChange={(e) => setEditTitle(e.target.value)}
									onKeyDown={handleTitleKeyDown}
									onBlur={handleTitleSave}
									className="flex-1 bg-transparent border-b-2 border-primary text-lg font-semibold leading-tight outline-none"
								/>
								<Button variant="ghost" size="icon" className="size-6" onClick={handleTitleSave}>
									<Check className="size-4" />
								</Button>
								<Button variant="ghost" size="icon" className="size-6" onClick={handleTitleCancel}>
									<X className="size-4" />
								</Button>
							</div>
						) : (
							<h2
								className="text-lg font-semibold leading-tight cursor-pointer hover:text-primary group flex items-center gap-2"
								onClick={() => setIsEditingTitle(true)}
							>
								{link.title}
								{link.archivedAt && (
									<span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono border border-muted-foreground/30 bg-muted text-muted-foreground">
										<Archive className="size-3" />
										[ARCHIVED]
									</span>
								)}
								{link.snoozedUntil && new Date(link.snoozedUntil).getTime() > Date.now() && (
									<span
										className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-mono border border-amber-500/40 bg-amber-500/10 text-amber-500"
										title={`Snoozed until ${new Date(link.snoozedUntil).toLocaleString()}`}
									>
										<AlarmClock className="size-3" />
										[SNOOZED]
									</span>
								)}
								<Pencil className="size-3 opacity-0 group-hover:opacity-50" />
							</h2>
						)}

						{/* URL */}
						{isEditingUrl ? (
							<input
								ref={urlInputRef}
								type="text"
								value={editUrl}
								onChange={(e) => setEditUrl(e.target.value)}
								onKeyDown={handleUrlKeyDown}
								onBlur={handleUrlSave}
								className="w-full text-sm font-mono bg-background border border-primary/50 px-2 py-1 mt-2 text-primary outline-none focus:ring-1 focus:ring-primary"
							/>
						) : (
							<div className="group/url flex items-center gap-1 mt-2">
								<a
									href={link.url}
									target="_blank"
									rel="noopener noreferrer"
									className="text-sm text-primary hover:underline break-all"
								>
									{link.url}
									<ExternalLink className="size-3 shrink-0 inline ml-1" />
								</a>
								<CopyUrlButton url={link.url} />
								<button
									onClick={() => setIsEditingUrl(true)}
									className="opacity-0 group-hover/url:opacity-50 hover:!opacity-100 transition-opacity ml-1"
									title="Edit URL"
								>
									<Pencil className="size-3" />
								</button>
							</div>
						)}
					</div>

					{/* Clear float - everything below gets full width */}
					<div className="clear-both" />

					{/* Tags section - full width, right under thumbnail */}
					<div className="flex items-start gap-2">
						<div className="flex-1">
							{link.tags && link.tags.length > 0 ? (
								<TagList tags={link.tags as TagData[]} showAll size="md" onTagClick={onTagClick} />
							) : (
								<span className="text-sm text-muted-foreground italic">No tags</span>
							)}
						</div>
						<button
							type="button"
							onClick={() => setShowTagEditor(true)}
							className="text-xs text-muted-foreground hover:text-primary shrink-0"
							title="Press L to edit tags"
						>
							edit
						</button>
					</div>

					{/* Queue row */}
					<div className="flex flex-wrap items-center gap-3">
						<button
							type="button"
							onClick={onQueueChange}
							className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-mono cursor-pointer hover:text-primary transition-colors border"
							style={{
								backgroundColor: 'transparent',
								color: currentQueue?.color ?? '#6366f1',
								borderColor: currentQueue?.color ?? '#6366f1',
							}}
							title="Press Q to change queue"
						>
							{currentQueue?.id === 0 ? (
								<Inbox className="size-3" />
							) : (
								<Layers className="size-3" />
							)}
							[{(currentQueue?.name ?? 'Incoming').toUpperCase()}]
						</button>
					</div>

					{/* Metadata row */}
					<div className="flex flex-wrap items-center gap-3 text-sm">
						<div className="flex items-center gap-1.5 text-muted-foreground">
							<Calendar className="size-4" />
							<span>{createdDate}</span>
						</div>
						{link.source && (
							<div className="flex items-center gap-1.5 text-muted-foreground">
								<Database className="size-4" />
								<span>{link.source}</span>
							</div>
						)}
					</div>

					{/* Description */}
					<div className="rounded-md border bg-muted/30">
						{isEditingDescription ? (
							<div className="p-4 space-y-2">
								<div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
									<span>Edit Markdown (Ctrl+Enter to save, Esc to cancel)</span>
									<div className="flex gap-1">
										<Button
											variant="ghost"
											size="sm"
											className="h-6 text-xs"
											onClick={handleDescriptionSave}
										>
											<Check className="size-3 mr-1" />
											Save
										</Button>
										<Button
											variant="ghost"
											size="sm"
											className="h-6 text-xs"
											onClick={handleDescriptionCancel}
										>
											<X className="size-3 mr-1" />
											Cancel
										</Button>
									</div>
								</div>
								<textarea
									ref={descriptionRef}
									value={editDescription}
									onChange={(e) => setEditDescription(e.target.value)}
									onKeyDown={handleDescriptionKeyDown}
									onBlur={handleDescriptionSave}
									className="w-full min-h-[200px] p-3 font-mono text-sm bg-background border rounded-md resize-y outline-none focus:ring-2 focus:ring-primary/50"
									placeholder="Enter description in Markdown format..."
								/>
							</div>
						) : (
							<div className="p-4 hover:bg-muted/50 transition-colors group min-h-[80px] relative">
								{editDescription ? (
									<div className="prose prose-sm dark:prose-invert max-w-none [&_a]:text-primary [&_a]:underline [&_a]:cursor-pointer pb-6">
										<MarkdownContent compact={false} url={link.url}>{editDescription}</MarkdownContent>
									</div>
								) : (
									<p className="text-sm text-muted-foreground italic pb-6">
										No description. Click edit to add one.
									</p>
								)}
								<button
									type="button"
									onClick={() => setIsEditingDescription(true)}
									className="absolute bottom-2 right-2 text-xs text-muted-foreground hover:text-foreground opacity-50 group-hover:opacity-100 transition-opacity"
								>
									edit
								</button>
							</div>
						)}
					</div>

					{/* Download Section */}
					<DownloadSection linkId={link.id} ref={downloadSectionRef} />

					{/* Public Access Panel */}
					<LinkPublicAccessPanel linkId={link.id} />

					{/* Metadata (JSON) */}
					<details className="group">
						<summary className="cursor-pointer text-sm font-mono text-muted-foreground hover:text-foreground">
							[METADATA] ({link.metadata ? Object.keys(link.metadata).length : 0} fields)
						</summary>
						<ObjectViewer data={link.metadata ?? {}} />
					</details>
				</div>
			</ScrollArea>

			{/* Tag Editor Modal */}
			<TagEditor
				open={showTagEditor}
				onOpenChange={setShowTagEditor}
				linkId={link.id}
				currentTags={
					(link.tags as {
						id: number;
						name: string;
						color: string | null;
						icon: string | null;
						isPersonal?: boolean;
					}[]) ?? []
				}
				onTagsChanged={(updatedTags) => {
					debug('tags changed: %o', updatedTags);
					onLinkUpdated?.({
						...link,
						tags: updatedTags,
					});
				}}
			/>

			{/* Enrich Options Modal */}
			<EnrichOptionsModal
				open={showEnrichOptions}
				onOpenChange={setShowEnrichOptions}
				onSubmit={handleEnrich}
			/>

			{/* Error Dialog */}
			<ErrorDialog
				open={enrichError !== null}
				onOpenChange={(open) => !open && setEnrichError(null)}
				title="Enrichment Failed"
				error={enrichError?.error ?? null}
				details={enrichError?.details}
			/>

			{/* Share Link Dialog */}
			<ShareLinkDialog
				open={showShareDialog}
				onOpenChange={setShowShareDialog}
				linkId={link.id}
				linkTitle={link.title}
			/>

			{/* Snooze Duration Picker */}
			<SnoozeDurationPicker
				open={showSnoozePicker}
				onOpenChange={setShowSnoozePicker}
				onSelect={handleSnooze}
			/>
		</div>
	);
}
